/**
 * Tunables for the runtime recovery system.
 *
 * Build-time constants for now. There is no remote-config or feature-flag
 * mechanism in this codebase, so promoting these to runtime configuration is a
 * separate piece of work — see docs/web-runtime-recovery-system.md §10.
 */
import { GLOBAL_VISIBILITY_RESYNC_THRESHOLD_MS } from '@/service/ws/visibility-policy'

import type { RecoveryLevel, RecoveryReason } from './types'

export { GLOBAL_VISIBILITY_RESYNC_THRESHOLD_MS }

// ─── Feature gates ──────────────────────────────────────────────────────────
// Level 2 and Level 3 are fully implemented (see recovery-controller.ts) but
// OFF by default. Flipping these on reloads a trading platform out from under
// a real session — that's a product/ops decision, not something to default to
// true just because the code exists. Turn on only after the Phase 1+2
// telemetry (level3Candidate frequency, safeToReload rate, reload-loop
// frequency) has been reviewed.
export const ENABLE_LEVEL_2 = true
export const ENABLE_LEVEL_3 = true

// ─── Health score weights (docs §5) ─────────────────────────────────────────
export const SCORE_TRANSPORT_STALE = 2
export const SCORE_RESYNC_FAILED = 2
export const SCORE_LAF_SEVERE = 2
export const SCORE_MEMORY_OVER = 2
export const SCORE_BUDGET_OVER = 1
export const SCORE_LIFECYCLE_GAP = 1
/**
 * Shadow-only. A stale data stream cannot be distinguished from a quiet market
 * using a single global timestamp, so it contributes to the score but never
 * triggers an automatic restart on its own. See docs §4 / Step 4 of the plan.
 */
export const SCORE_DATA_STALE = 1

// ─── Thresholds ─────────────────────────────────────────────────────────────
/**
 * No decoded frame at all — pings included — for this long means the transport
 * is genuinely dead. The server pings on a fixed cadence, so unlike
 * DATA_STALE_MS this is a trustworthy disconnection signal.
 */
export const TRANSPORT_STALE_MS = 90_000
/** Diagnostic only; see SCORE_DATA_STALE. */
export const DATA_STALE_MS = 5 * 60_000
/** A single frame this long is "severe" for scoring purposes. */
export const LAF_SEVERE_MS = 200
/** How many severe frames within the sampling window before it counts. */
export const LAF_SEVERE_COUNT = 3
export const HEAP_USAGE_RATIO_LIMIT = 0.9
export const QUERY_BUDGET = 600
export const WIDGET_BUDGET = 40
/**
 * An unexplained gap in execution (no timers ran, but we were not hidden for
 * that long either) implies the tab was frozen or discarded.
 */
export const LIFECYCLE_GAP_MS = 60_000

// ─── Timing ─────────────────────────────────────────────────────────────────
/**
 * Minimum interval between re-assessments. This is a "don't re-evaluate yet"
 * window, not a "drop the request" window — reasons arriving inside it are
 * merged into the in-flight cycle rather than discarded.
 */
export const RECOVERY_DEDUPE_MS = 2_000
/** How long a Level 1 waits for every account domain to re-dump. */
export const RESYNC_ACK_TIMEOUT_MS = 15_000
/** How long restart() waits for the socket to re-authenticate. */
export const RESTART_TIMEOUT_MS = 15_000
/** Delay between close and reopen inside restart(), so the close propagates. */
export const RESTART_RECONNECT_DELAY_MS = 100
/** How long to observe long frames after returning, before the 2nd assessment. */
export const LAF_SAMPLE_WINDOW_MS = 3_000
/** Ring-buffer size for lag samples. */
export const LAG_SAMPLE_CAPACITY = 32

// ─── Level 3 policy ──────────────────────────────────────────────────────────
// No per-session attempt cap — see reload-guard.ts's file doc comment for why.
export const RELOAD_COOLDOWN_MS = 10 * 60_000
/**
 * A socket that re-authenticates successfully while account dump ACKs still time out
 * points at the backend stream path, not stale browser state. Reloading cannot repair
 * that dependency, so suppress stream-failure-driven reloads until either a later
 * recovery succeeds or this window expires.
 */
export const BACKEND_STREAM_RELOAD_SUPPRESSION_MS = 30 * 60_000

// ─── Telemetry ──────────────────────────────────────────────────────────────
/** Minimum gap between telemetry events for the same recovery level. */
export const TELEMETRY_MIN_INTERVAL_MS = 30_000

/**
 * Scale the heap-usage limit by device class. Low-memory devices hit trouble at
 * a lower absolute usage, so they get a tighter ratio.
 */
export function memoryLimitForDevice(deviceMemoryGb: number | null): number {
  if (deviceMemoryGb === null) return HEAP_USAGE_RATIO_LIMIT
  if (deviceMemoryGb <= 2) return 0.75
  if (deviceMemoryGb <= 4) return 0.85
  return HEAP_USAGE_RATIO_LIMIT
}

/**
 * Map a health score to a recovery level (docs §5).
 *
 * This is guidance for picking a level, not ground truth — Level 3 in
 * particular is never chosen on score alone.
 */
export function levelForScore(score: number): RecoveryLevel {
  if (score <= 1) return 0
  if (score <= 3) return 1
  return 2
}

// ─── Debug-mode hold ────────────────────────────────────────────────────────

/**
 * Debug mode holds recovery so Level 1/2 don't reset the counters the terminal
 * watches. `network-recovered` is exempt: its caller skips its own `connect()`
 * once a controller has accepted the request, so holding it leaves a dead
 * socket with nothing left to reconnect it.
 */
export function shouldHoldForDebug(reasons: RecoveryReason[], trackingActive: boolean): boolean {
  return trackingActive && !reasons.includes('network-recovered')
}
